import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../common/Card';
import Button from '../common/Button';
// import FormSidebar from './FormSidebar';
import FormSection from './FormSection';
import CC4FileUpload from './CC4FileUpload';
import VO2DataTable from './VO2DataTable';
import TrainingZonesSection from './TrainingZonesSection';
import Spinner from '../common/Spinner';
import { reportService } from '../../services/reportService';
import { vo2MaxReportService } from '../../services/vo2MaxReportService';
import { patientService } from '../../services/patientService';
import Input from '../common/Input';
import Select from '../common/Select';
import { format } from 'date-fns';
import { uploadImageToS3 } from '../../utils/uploadImageToS3';
import { requestPDF } from '../../services/pdfService';
import PublishingOverlay from '../common/PublishingOverlay';
import { generateVO2MaxReportHTML } from '../../services/vo2MaxReportService';
import ValidationModal from './ValidationModal';
import { validateVO2MaxReport } from '../../utils/vo2maxValidation';
import { generatePDFFilename } from '../../utils/generatePDFFilename';
import { Line } from 'react-chartjs-2';
import { extractVO2DataFromImage } from '../../services/mistralService';
import { FaUpload, FaSpinner } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Utility function to calculate average of an array
const average = (arr) => arr.reduce((sum, val) => sum + val, 0) / arr.length;

const calculateVO2 = (sample) => {
    const FiO2 = 0.2093; // Standard ambient Oxygen
    const FiCO2 = 0.0004; // Standard ambient CO2
    
    const Ve = parseFloat(sample.ve);
    const FeO2 = parseFloat(sample.feo2) / 100;
    const FeCO2 = parseFloat(sample.feco2 || 0) / 100;

    // 1. Calculate Inspired Volume (Haldane Transformation)
    const Vi = Ve * ((1 - FeO2 - FeCO2) / (1 - FiO2 - FiCO2));

    // 2. Calculate Absolute VO2 in Liters
    const vo2Liters = (Vi * FiO2) - (Ve * FeO2);

    // 3. Convert to mL/min
    return Math.round(vo2Liters * 1000);
};


// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ImageUpload = ({ label, value, onChange, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');

  const handleImageChange = async (e) => {
    if (disabled || uploading) return;
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    setUploading(true);
    try {
      setPreview(URL.createObjectURL(file));
      const s3Url = await uploadImageToS3(file);
      setPreview(s3Url);
      onChange(s3Url);
    } catch (err) {
      alert('Failed to upload image.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    if (disabled || uploading) return;
    setPreview('');
    onChange('');
  };

  return (
    <div className="flex flex-col items-center mb-2">
      {preview ? (
        <img
          src={preview}
          alt={label}
          className="w-28 h-40 object-contain border rounded mb-1"
        />
      ) : (
        <div className="w-28 h-40 flex items-center justify-center border rounded bg-gray-100 text-gray-400 mb-1 text-xs">
          No Image
        </div>
      )}
      <input
        type="file"
        accept="image/*"
        disabled={disabled || uploading}
        onChange={handleImageChange}
        className="block text-xs"
        style={{ width: "110px" }}
      />
      {uploading && <span className="text-xs text-primary-600 mt-1">Uploading...</span>}
      <span className="text-xs text-gray-500">{label}</span>
      {preview && !disabled && (
        <Button
          variant="secondary"
          className="mt-1"
          onClick={handleRemoveImage}
          disabled={uploading}
        >
          Remove Image
        </Button>
      )}
    </div>
  );
};

// Helper function to format numbers
const formatNumber = (id) => {
  return id ? id.toString().padStart(4, '0') : '';
};

// VO2 Graph component
const VO2Graph = ({ stageData, thresholds }) => {
  if (!stageData || !stageData.length) {
    return (
      <div className="p-4 border rounded bg-gray-50 text-center">
        <p>No data available for graph. Upload CC4 file or enter data manually.</p>
      </div>
    );
  }

  // Sort data by time
  const sortedData = [...stageData].sort((a, b) => a.testTime - b.testTime);

  // Prepare data for chart
  const labels = sortedData.map(item => item.testTime);
  
  // Get threshold values from props or use defaults if not available
  const aerobicThreshold = thresholds?.aerobic || null;
  const anaerobicThreshold = thresholds?.anaerobic || null;
  
  const datasets = [
    {
      label: 'Heart Rate',
      data: sortedData.map(item => item.hr),
      borderColor: 'rgb(75, 192, 75)',
      backgroundColor: 'rgba(75, 192, 75, 0.5)',
      tension: 0.3,
    },
    {
      label: 'VO2',
      data: sortedData.map(item => item.vo2),
      borderColor: 'rgb(53, 53, 53)',
      backgroundColor: 'rgba(53, 53, 53, 0.5)',
      tension: 0.3,
    }
  ];
  
  // Only add threshold lines if values are available
  if (aerobicThreshold) {
    datasets.push({
      label: 'Aerobic Threshold',
      data: sortedData.map(() => aerobicThreshold),
      borderColor: 'rgb(153, 102, 255)',
      backgroundColor: 'rgba(153, 102, 255, 0.5)',
      borderDash: [5, 5],
    });
  }
  
  if (anaerobicThreshold) {
    datasets.push({
      label: 'Anaerobic Threshold',
      data: sortedData.map(() => anaerobicThreshold),
      borderColor: 'rgb(255, 99, 132)',
      backgroundColor: 'rgba(255, 99, 132, 0.5)',
      borderDash: [5, 5],
    });
  }
  
  const data = {
    labels,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'VO2 Max Graph',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
      x: {
        title: {
          display: true,
          text: 'Time (seconds)',
        },
      },
    },
  };

  return (
    <div className="p-4 border rounded bg-white" style={{ height: '400px' }}>
      <Line data={data} options={options} />
    </div>
  );
};

const VO2MaxReportForm = () => {
  const navigate = useNavigate();
  const { id, patientId } = useParams(); // Extract both id and patientId from URL params
  const isEditMode = !!id;
  const appointmentId = patientId; // Use patientId as appointmentId
  let pdf_url = null;
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [publishingStatus, setPublishingStatus] = useState(null);
  const [patient, setPatient] = useState(null);
  const [stageData] = useState([]);
  const [formData, setFormData] = useState({
    // Patient info
    patient_id: '',
    patient_name: '',
    patient_gender: '',
    patient_age: '',
    patient_height: '',
    patient_weight: '',
    exam_date: format(new Date(), 'yyyy-MM-dd'),
    
    // VO2Max data
    vo2max_value: '',
    vo2max_unit: 'ml/kg/min',
    heart_rate_max: '',
    heart_rate_rest: '',
    
    // RMR data
    rmr_value: '',
    rmr_unit: 'kcal/day',
    
    // VO2 Data Table - updated to match screenshot and sample data
    vo2_data_table: [
      { 
        stage: 1,
        time: '',
        hr: '',
        incline: '',
        speed: '',
        vo2: '',
        vco2: '',
        ve_vo2: '',
        ve_vco2: '',
        rr: '',
        ve: '',
        tv: '',
        fe_o2: '',
        fe_co2: '',
        watts: ''
      }
    ],
    
    // Raw stage data from CC4 file
    stage_data: [],
    
    // Graph data
    graph_data: {
      heart_rate: [],
      vo2: [],
      aerobic_threshold: null,
      anaerobic_threshold: null
    },
    
    
    // Training Zones - Simplified to BPM only
    training_zones: {
      recovery_1min: '',
      recovery_2min: '',
      zone1_name: 'RECOVERY MODE',
      zone1_bpm: '',
      zone2_name: 'AEROBIC BASE',
      zone2_bpm: '',
      zone3_name: 'AEROBIC CAPACITY',
      zone3_bpm: '',
      zone4_name: 'THRESHOLD ZONE',
      zone4_bpm: '',
      zone5_name: 'VO2 MAX ZONE',
      zone5_bpm: ''
    },
    
    // Threshold data
    aerobic_threshold_hr: '',
    aerobic_threshold_percent: '',
    lactate_threshold_hr: '',
    lactate_threshold_percent: '',
    anaerobic_threshold_hr: '',
    anaerobic_threshold_percent: '',
    
    // Additional notes
    notes: '',
    recommendations: '',
    
    // Report status
    status: 'draft',
  });

  const [currentSection, setCurrentSection] = useState(0);
  const [isEditingAllowed, setIsEditingAllowed] = useState(true);
  const [validationModal, setValidationModal] = useState({ isOpen: false, violations: [], action: null });
  const [bypassValidation, setBypassValidation] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Define form sections - Patient Information, VO2 Graph, Training Zones, and VO2 Data Table in that order
  const sections = [
    {
      id: 'patient_info',
      label: 'Patient Information',
      fields: [
        { name: 'patient_id', label: 'Patient ID', type: 'text'},
        { name: 'patient_name', label: 'Patient Name', type: 'text'},
        { name: 'patient_gender', label: 'Gender', type: 'select', options: [
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' },
          { value: 'other', label: 'Other' },
        ]},
        { name: 'patient_age', label: 'Age', type: 'number' },
        { name: 'vo2max_value', label: 'VO2 Max (ml/kg/min)', type: 'number', highlight: true },
        { name: 'rmr_value', label: 'Resting Metabolic Rate (kcal/day)', type: 'number', highlight: true },
        { name: 'heart_rate_max', label: 'Max Heart Rate (bpm)', type: 'number' },
        // { name: 'heart_rate_rest', label: 'Resting Heart Rate (bpm)', type: 'number' },
        { name: 'exam_date', label: 'Exam Date', type: 'date' },
      ],
    },
    // {
    //   id: 'vo2_graph',
    //   label: 'VO2 Max Graph',
    //   component: 'vo2_graph',
    //   description: 'Visual representation of VO2 test data',
    // },
    {
      id: 'training_zones',
      label: 'Training Zones',
      description: 'Enter BPM values for each training zone',
      component: 'training_zones',
      fields: [
        { name: 'recovery_1min', label: 'Recovery After 1 Min (BPM)', type: 'number' },
        { name: 'recovery_2min', label: 'Recovery After 2 Min (BPM)', type: 'number' },
        { name: 'training_zones.zone1_name', label: 'Zone 1: RECOVERY MODE', type: 'text', defaultValue: 'RECOVERY MODE', disabled: true },
        { name: 'training_zones.zone1_bpm', label: 'Zone 1 BPM', type: 'text', placeholder: 'e.g. <138 BPM' },
        { name: 'training_zones.zone2_name', label: 'Zone 2: AEROBIC BASE', type: 'text', defaultValue: 'AEROBIC BASE', disabled: true },
        { name: 'training_zones.zone2_bpm', label: 'Zone 2 BPM', type: 'text', placeholder: 'e.g. 138-164 BPM' },
        { name: 'training_zones.zone3_name', label: 'Zone 3: AEROBIC CAPACITY', type: 'text', defaultValue: 'AEROBIC CAPACITY', disabled: true },
        { name: 'training_zones.zone3_bpm', label: 'Zone 3 BPM', type: 'text', placeholder: 'e.g. 164-175 BPM' },
        { name: 'training_zones.zone4_name', label: 'Zone 4: THRESHOLD ZONE', type: 'text', defaultValue: 'THRESHOLD ZONE', disabled: true },
        { name: 'training_zones.zone4_bpm', label: 'Zone 4 BPM', type: 'text', placeholder: 'e.g. 175-176 BPM' },
        { name: 'training_zones.zone5_name', label: 'Zone 5: VO2 MAX ZONE', type: 'text', defaultValue: 'VO2 MAX ZONE', disabled: true },
        { name: 'training_zones.zone5_bpm', label: 'Zone 5 BPM', type: 'text', placeholder: 'e.g. >176 BPM' },
      ],
    },
    {
      id: 'vo2_data_table',
      label: 'VO2 Data Table',
      component: 'vo2_data_table',
      description: 'Enter the VO2 test data points',
    },
  ];

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isEditMode) {
          // Fetch report data
          const reportData = await reportService.getReportById(id);
          
          // Fetch patient data
          const patientData = await patientService.getPatientById(reportData.patient_id);
          
          setPatient(patientData);
          setFormData({
            ...reportData,
            patient_id: patientData.id,
            patient_name: `${patientData.first_name} ${patientData.last_name}`,
          });
          
          // Always allow editing
          setIsEditingAllowed(true);
        } else {
          // Using appointmentId from URL params (extracted via useParams)
          
          if (appointmentId) {
            try {
              // Fetch VO2Max report data by appointment ID
              console.log('Fetching VO2Max report for appointment ID:', appointmentId);
              const response = await vo2MaxReportService.getReportByAppointmentId(appointmentId);
              
              // Extract the actual data from the response
              const reportData = response?.data;
              console.log('Received report data:', reportData);
              
              if (reportData) {
                // Set form data from the API response
                setFormData({
                  ...reportData,
                  appointment_id: appointmentId,
                });
                
                setIsEditingAllowed(true);
                
                // Fetch patient data if patient_id is available
                if (reportData.patient_id) {
                  const patientData = await patientService.getPatientById(reportData.patient_id);
                  setPatient(patientData);
                }
              }
            } catch (error) {
              console.error('Error fetching VO2Max report:', error);
              // If no report exists yet, just set the appointment ID
              setFormData(prev => ({
                ...prev,
                appointment_id: appointmentId,
              }));
              
              // Try to get patient data from the appointment ID
              try {
                console.log('Fetching patient data for appointment ID:', appointmentId);
                // First try to get appointment data to find patient ID
                const appointmentData = await fetch(`https://vitaldev.vitalinsights.in/admin/appointments/${appointmentId}`, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                }).then(res => res.json());
                
                console.log('Appointment data:', appointmentData);
                
                if (appointmentData && appointmentData.patient_id) {
                  // Now get patient data using patient ID from appointment
                  const patientData = await patientService.getPatientById(appointmentData.patient_id);
                  console.log('Patient data:', patientData);
                  
                  setPatient(patientData);
                  setFormData(prev => ({
                    ...prev,
                    patient_id: patientData.id,
                    patient_name: `${patientData.first_name} ${patientData.last_name}`,
                    patient_gender: patientData.gender || '',
                    patient_age: patientData.age || '',
                    patient_height: patientData.height || '',
                    patient_weight: patientData.weight || '',
                    exam_date: format(new Date(), 'yyyy-MM-dd'),
                  }));
                } else {
                  // Fallback to using appointment ID as patient ID
                  const patientData = await patientService.getPatientById(appointmentId);
                  setPatient(patientData);
                  setFormData(prev => ({
                    ...prev,
                    patient_id: patientData.id,
                    patient_name: `${patientData.first_name} ${patientData.last_name}`,
                    patient_gender: patientData.gender || '',
                    patient_age: patientData.age || '',
                  }));
                }
              } catch (patientError) {
                console.error('Error fetching patient data:', patientError);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, isEditMode, appointmentId]);

  // Helper functions to get and set nested values
  const getNestedValue = (obj, path) => {
    if (!path) return obj;
    
    const keys = path.split('.');
    let result = obj;
    
    for (const key of keys) {
      if (result === null || result === undefined) {
        return undefined;
      }
      result = result[key];
    }
    
    return result;
  };
  
  const setNestedValue = (obj, path, value) => {
    if (!path) return obj;
    
    const keys = path.split('.');
    const lastKey = keys.pop();
    let current = obj;
    
    // Navigate to the right level
    for (const key of keys) {
      if (current[key] === undefined) {
        current[key] = {};
      }
      current = current[key];
    }
    
    // Set the value
    current[lastKey] = value;
    return obj;
  };

  // Handle form field changes
  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      return setNestedValue(newData, field, value);
    });
  };

  // Handle image upload button click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle image file selection and processing
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should be less than 10MB');
      return;
    }

    setIsProcessingImage(true);
    
    try {
      // Extract data from image using Mistral AI
      const extractedData = await extractVO2DataFromImage(file);
      
      // Update the form data with extracted VO2 table data
      handleFormChange('vo2_data_table', extractedData);
      
      toast.success(`Successfully extracted ${extractedData.length} rows from the image`);
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error(error.message || 'Failed to extract data from image');
    } finally {
      setIsProcessingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (status = 'submitted') => {
    // Validate report unless bypass is enabled
    if (!bypassValidation) {
      const validation = validateVO2MaxReport(formData);
      if (!validation.isValid) {
        setValidationModal({
          isOpen: true,
          violations: validation.violations,
          action: status
        });
        return;
      }
    }

    setSubmitting(true);
    
    try {
      // If status is 'published', generate PDF first
      
      if (status === 'published') {
        setPublishingStatus('generating');
        try {
          // Generate HTML for the report
          const html = await generateVO2MaxReportHTML(formData);
          
          // Convert HTML to PDF using the pdfService
          const pdfBlob = await requestPDF(html);
          
          // Create a URL for the PDF blob
          const pdfObjectUrl = URL.createObjectURL(pdfBlob);
          
          // Upload the PDF to S3
          const patientName = patient?.profile?.name || formData.patient_name || patient?.name || 'patient';
          const orderId = patient?.sukra_order_id || patient?.id || 'unknown';
          const fileName = generatePDFFilename(patientName, 'vo2max', orderId);
          console.log('Uploading PDF with filename:', fileName);
          
          try {
            // Create a File object from the Blob
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
            console.log('Created PDF file object:', pdfFile);
            
            // Upload directly to the server
            console.log('Starting direct upload...');
            
            // Create FormData for direct upload
            const formData = new FormData();
            formData.append('file', pdfFile);
            formData.append('file_name', fileName);
            
            // Get token for authentication
            const token = localStorage.getItem('token');
            
            // Upload directly to backend
            console.log('Uploading file with token:', token ? 'Token exists' : 'No token');
            
            const uploadRes = await fetch('https://vitaldev.vitalinsights.in/user/upload-file-to-s3', {
              method: 'POST',
              body: formData,
              headers: {
                'Authorization': `Bearer ${token}`
              }
              // Note: Don't set Content-Type header, browser will set it automatically with boundary
            });

            console.log('Upload response:', JSON.stringify(uploadRes));
            
            if (!uploadRes.ok) {
              const errorText = await uploadRes.text();
              console.error('Upload error response:', errorText);
              throw new Error(`Upload failed with status ${uploadRes.status}: ${errorText}`);
            }
            
            const response = await uploadRes.json();
            console.log('Upload response:', JSON.stringify(response));
            
            if (!response.fileUrl) throw new Error('Invalid response from server');
            
            // Set the PDF URL - ensure it's a string
            if (typeof response.fileUrl === 'string') {
              pdf_url = response.fileUrl;
              console.log('Using uploaded URL:', pdf_url);
            } else {
              // If the response is not a string, convert it to a string
              pdf_url = String(response.fileUrl);
              console.log('Converted URL to string:', pdf_url);
            }
          } catch (uploadError) {
            console.error('Error during PDF upload:', uploadError);
            
            // Fallback to object URL if upload fails
            pdf_url = pdfObjectUrl;
            console.warn('Using object URL as fallback:', pdf_url);
          }
          
          console.log('PDF generated and uploaded:', pdf_url);
        } catch (error) {
          console.error('Error generating PDF:', error);
          setPublishingStatus('error');
          toast.error('Failed to generate PDF');
          setSubmitting(false);
          return;
        }
      }
      
      // Create the submission data
      const dataToSubmit = {
        ...formData,
        status,
        report_type: 'vo2max',
        appointment_id: formData.appointment_id || appointmentId, // Ensure appointment_id is included
      };
      
      // If we have a PDF URL, include it in the submission
      if (pdf_url) {
        // Make sure the PDF URL is properly set in the submission
        console.log('Setting PDF URL in submission:', pdf_url);
        
        // Set the PDF URL directly as a string property
        dataToSubmit.pdf_url = pdf_url;
        
        // Ensure the status is set to 'published' when we have a PDF
        dataToSubmit.status = 'published';
        dataToSubmit.report_status = 'published';
        
        // Log the final data to be submitted
        console.log('Final data with PDF URL:', JSON.stringify(dataToSubmit));
        console.log('PDF URL type:', typeof dataToSubmit.pdf_url);
        console.log('PDF URL value:', dataToSubmit.pdf_url);
      } else {
        console.warn('No PDF URL available for submission');
      }
      
      console.log('Submitting data:', dataToSubmit);
      
      // Double-check that pdf_url is included in the request if it should be
      if (status === 'published' && !dataToSubmit.pdf_url) {
        console.error('PDF URL is missing from the submission data!');
        toast.error('Failed to include PDF URL in submission');
        setSubmitting(false);
        if (status === 'published') {
          setPublishingStatus('error');
        }
        return;
      }
      
      // Always use the POST API (createReport) regardless of edit mode
      // Make a direct API call to ensure the PDF URL is included
      try {
        console.log('Making direct API call with data:', dataToSubmit);
        
        // Create the payload with PDF URL directly in the root object
        const payload = {
          data: {
            ...dataToSubmit
          }
        };
        
        // Ensure PDF URL is explicitly included at the top level
        if (dataToSubmit.pdf_url) {
          payload.data.pdf_url = dataToSubmit.pdf_url;
          console.log('PDF URL in final payload:', payload.data.pdf_url);
        }
        
        // Make the API call directly
        const response = await fetch('https://vitaldev.vitalinsights.in/admin/vo2max-reports', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const responseData = await response.json();
        console.log('API response:', responseData);
      } catch (apiError) {
        console.error('Error making direct API call:', apiError);
        throw apiError;
      }
      
      toast.success('Report submitted successfully');
      
      if (status === 'published') {
        setPublishingStatus('success');
        
        // Wait a moment before redirecting to patients page
        setTimeout(() => {
          setSubmitting(false);
          setPublishingStatus(null);
          // Redirect to patients page
          navigate('/patients');
        }, 2000);
      } else {
        // Don't redirect, stay on the same page
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form');
      setSubmitting(false);
      if (status === 'published') {
        setPublishingStatus('error');
      }
    }
  };
  
  // We're now directly using uploadImageToS3 in the handleSubmit function

  // Handle cancel button
  const handleCancel = () => {
    navigate('/reports');
  };

  const handleForceCreate = () => {
    setBypassValidation(true);
    setValidationModal({ isOpen: false, violations: [], action: null });

    // Execute the pending action
    if (validationModal.action === 'preview') {
      handlePreview();
    } else if (validationModal.action === 'submitted' || validationModal.action === 'published') {
      handleSubmit(validationModal.action);
    }
  };

  // Handle section navigation
  const handleSectionChange = (index) => {
    setCurrentSection(index);
  };
  
  const goToNextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };
  
  const goToPreviousSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  // Handle report preview
  const handlePreview = async () => {
    // Validate report unless bypass is enabled
    if (!bypassValidation) {
      const validation = validateVO2MaxReport(formData);
      if (!validation.isValid) {
        setValidationModal({
          isOpen: true,
          violations: validation.violations,
          action: 'preview'
        });
        return;
      }
    }

    try {
      // Store form data in session storage
      sessionStorage.setItem('vo2maxReportData', JSON.stringify(formData));
      
      // Open preview page in new tab
      const previewUrl = `/vo2max-report/${appointmentId || patientId}/preview`;
      window.open(previewUrl, '_blank');
    } catch (error) {
      console.error('Error opening preview:', error);
      toast.error('Failed to open preview');
    }
  };

  // Render form fields based on type
  const renderField = (field) => {
    const value = getNestedValue(formData, field.name);
    
    switch (field.type) {
      case 'text':
      case 'number':
      case 'date':
        return (
          <div key={field.name} className={`mb-4 ${field.highlight ? 'p-3 bg-yellow-50 border border-yellow-200 rounded-md' : ''}`}>
            <Input
              label={field.highlight ? `${field.label} *` : field.label}
              type={field.type}
              value={value || ''}
              onChange={(e) => handleFormChange(field.name, e.target.value)}
              disabled={field.disabled || !isEditingAllowed}
              className={field.highlight ? 'font-bold' : ''}
            />
          </div>
        );
        
      case 'select':
        return (
          <div key={field.name} className="mb-4">
            <Select
              label={field.label}
              value={value || ''}
              onChange={(e) => handleFormChange(field.name, e.target.value)}
              disabled={field.disabled || !isEditingAllowed}
              options={field.options}
            />
          </div>
        );
        
      case 'textarea':
        return (
          <div key={field.name} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <textarea
              value={value || ''}
              onChange={(e) => handleFormChange(field.name, e.target.value)}
              disabled={!isEditingAllowed}
              className="w-full p-2 border rounded"
              rows={5}
            />
          </div>
        );
        
      case 'image':
        return (
          <div key={field.name} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <ImageUpload
              label={field.label}
              value={value}
              onChange={(img) => handleFormChange(field.name, img)}
              disabled={!isEditingAllowed}
            />
          </div>
        );
        
      case 'component':
        return field.component;
        
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {publishingStatus && (
        <PublishingOverlay status={publishingStatus} />
      )}
      <ValidationModal
        isOpen={validationModal.isOpen}
        violations={validationModal.violations}
        onClose={() => setValidationModal({ isOpen: false, violations: [], action: null })}
        onForceCreate={handleForceCreate}
        title="VO2Max Report Validation Failed"
      />
      
      <div className="flex flex-col md:flex-row gap-4 p-4">
        <div className="md:w-64 flex-shrink-0">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-xl font-bold text-primary-500 mb-4">Report Sections</h3>
            <ul className="space-y-2">
              {sections.map((section, index) => (
                <li key={section.id}>
                  <button
                    onClick={() => handleSectionChange(index)}
                    disabled={submitting}
                    className={`w-full text-left py-2 px-3 rounded transition ${currentSection === index 
                      ? 'bg-primary-500 text-white font-medium' 
                      : 'hover:bg-gray-100'}`}
                  >
                    {section.label}
                    {currentSection === index && (
                      <span className="float-right">→</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="flex-1">
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold">
                {isEditMode ? 'Edit VO2 Max Report' : 'New VO2 Max Report'}
              </h1>
              
              {patient && (
                <div className="text-right">
                  <div className="font-semibold">
                    {patient.first_name} {patient.last_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    ID: {formatNumber(patient.id)}
                  </div>
                </div>
              )}
            </div>
            
            {currentSection === 0 && (
              <CC4FileUpload
                onUploadSuccess={(cc4Data) => {
                    console.log('CC4 Data received:', cc4Data);
                    
                    // Process data samples for the graph and table
                    let processedStageData = [];
                    
                    

                    
                    // Get stage data from DATA_SAMPLES section for the table
                    if (cc4Data.dataSamples && cc4Data.dataSamples.length > 0) {
                      let stage = 0;
                      console.log(JSON.stringify(cc4Data.dataSamples))
                      for (let i = 0; i < cc4Data.dataSamples.length; i++) {
                        if (stage < 0) {
                          stage = cc4Data.dataSamples[i].stage;
                        }
                        
                        if (i !== 0 && stage >= -1 && cc4Data.dataSamples[i].stage !== stage  ) {
                         
                          const sample = cc4Data.dataSamples[i-1];

                          stage = cc4Data.dataSamples[i].stage;

                          const stageSamples1 = cc4Data.dataSamples.filter(d => d.stage === sample.stage);
                      
                      // Take the top 3 highest VO2 values (smoothing window)
                      // const window = stageSamples1;
                      // const window = stageSamples1
                      //   .filter(d => d.vo2 && !isNaN(parseFloat(d.vo2)))
                      //   .sort((a, b) => parseFloat(b.vo2) - parseFloat(a.vo2))
                      //   .slice(0, parseInt(stageSamples1.length/2));
                      // console.log('window', window);

                      const window = stageSamples1.slice(-3);
                      console.log('window', window);

                      
                      if (window.length > 0) {
                        const reportHR = average(window.map(d => typeof d.hr === 'number' ? d.hr : 0));
                        const reportVO2Abs = average(window.map(d => typeof d.vo2 === 'number' ? d.vo2 : 0));
                        const reportVe = average(window.map(d => typeof d.ve === 'number' ? d.ve : 0));

                          processedStageData.push({
                            stage: sample.stage ,
                            testTime: sample.time ? parseInt(sample.time) : '--',
                            seconds: sample.time ? parseInt(sample.time) : '--',
                            hr: reportHR ? parseInt(sample.hr) : '--',
                            vo2: reportVO2Abs ? parseFloat(reportVO2Abs) : '--',
                            vo2_ml: reportVO2Abs ? parseFloat(reportVO2Abs) : '--',
                            vo2_lkg: reportVO2Abs ? (parseFloat(reportVO2Abs || 0) / 64).toFixed(2) : '--', // Calculate L/kg/min based on mL/min
                            ve_vo2: reportVe && reportVO2Abs ? (parseFloat(reportVe || 0) / parseFloat(reportVO2Abs || 1) * 1000) : '--',
                            calories_min: sample.calories ? (parseFloat(sample.calories) || 0).toFixed(1) : '--',
                            calories_hr: sample.calories ? Math.round(parseFloat(sample.calories) * 60) : '--',
                            grade: sample.grade ? parseFloat(sample.grade) : '0',
                            speed: sample.speed ? parseFloat(sample.speed) : '--'
                          });
                        }

                        }

                      }
                      // Use data samples for the stage data table
                      // processedStageData = cc4Data.dataSamples.map((sample, index) => ({
                      //   stage: index + 1,
                      //   testTime: sample.time ? parseInt(sample.time) : '--',
                      //   seconds: sample.time ? parseInt(sample.time) : '--',
                      //   hr: sample.hr ? parseInt(sample.hr) : '--',
                      //   vo2: sample.vo2 ? parseFloat(sample.vo2) : '--',
                      //   vo2_ml: sample.vo2 ? parseFloat(sample.vo2) : '--',
                      //   vo2_lkg: sample.vo2 ? (parseFloat(sample.vo2 || 0) / 64).toFixed(2) : '--', // Calculate L/kg/min based on mL/min
                      //   ve_vo2: sample.ve && sample.vo2 ? (parseFloat(sample.ve || 0) / parseFloat(sample.vo2 || 1) * 1000) : '--',
                      //   calories_min: sample.calories ? (parseFloat(sample.calories) || 0).toFixed(1) : '--',
                      //   calories_hr: sample.calories ? Math.round(parseFloat(sample.calories) * 60) : '--',
                      //   grade: sample.stage ? parseFloat(sample.stage) : '--',
                      //   speed: sample.speed ? parseFloat(sample.speed) : '--'
                      // }));
                    } else {
                      // Fallback to manually creating stage data from the table data in the screenshot
                      // This is sample data based on the screenshot provided
                      processedStageData = [
                        { stage: 1, testTime: '--', hr: '--', vo2: '--', grade: '--', speed: '--', watts: '--' },
                        ];
                    }
                    
                    // Sort by test time
                    processedStageData.sort((a, b) => a.testTime - b.testTime);
                    
                    // Apply smoothing logic to get unique stages with smoothed values
                    const smoothedData = [];
                    const uniqueStages = [...new Set(processedStageData.map(d => d.stage))];
                    
                    uniqueStages.forEach(currentStage => {
                      const stageSamples = processedStageData.filter(d => d.stage === currentStage);
                      
                      // Take only the last 3 data points (smoothing window)
                      const window = stageSamples.slice(-3);
                      console.log('window', window);
                      
                      if (window.length > 0) {
                        const reportHR = average(window.map(d => typeof d.hr === 'number' ? d.hr : 0));
                        const reportVO2Abs = average(window.map(d => typeof d.vo2 === 'number' ? d.vo2 : 0));
                        const reportVO2Rel = reportVO2Abs / (cc4Data?.patientInfo?.weight ); // Patient Weight
                        const reportVeVO2 = average(window.map(d => typeof d.ve_vo2 === 'number' ? d.ve_vo2 : 0));
                        const reportGrade = window[window.length - 1].grade; // Last grade of stage
                        
                        smoothedData.push({
                          stage: currentStage,
                          testTime: window[window.length - 1].testTime,
                          hr: reportHR > 0 ? Math.round(reportHR) : '--',
                          vo2: reportVO2Abs > 0 ? Math.round(reportVO2Abs.toFixed(2)) : '--',
                          vo2_ml: reportVO2Abs > 0 ? Math.round(reportVO2Abs.toFixed(2)) : '--',
                          vo2_lkg: reportVO2Rel > 0 ? (reportVO2Rel.toFixed(2)) : '--',
                          ve_vo2: reportVeVO2 > 0 ? reportVeVO2.toFixed(2) : '--',
                          grade: reportGrade || '--',
                          speed: window[window.length - 1].speed || '--',
                          calories_min: window[window.length - 1].calories_min || '--',
                          calories_hr: window[window.length - 1].calories_hr || '--'
                        });
                      }
                    });
                    
                    // Create table data from the smoothed stage data
                    const tableData = smoothedData.map((item, index) => ({
                      stage: item.stage + 1,
                      time: `${Math.floor((item.testTime - 1) / 60)}:${((item.testTime - 1) % 60).toString().padStart(2, '0')}`,
                      hr: item.hr,
                      incline: item.grade,
                      speed: item.speed,
                      vo2_ml: item.vo2_ml,
                      vo2_lkg: item.vo2_lkg,
                      ve_vo2: item.ve_vo2,
                      calories_min: item.calories_min,
                      calories_hr: item.calories_hr
                    }));
                    
                    // Update form data with the processed stage data, table data, and other CC4 data
                    setFormData(prev => {
                      // Extract training zones from CC4 data
                      const trainingZones = { ...prev.training_zones };
                      
                      if (cc4Data.heartRateZones) {
                        trainingZones.zone1_bpm = `<${cc4Data.heartRateZones.hrZone0.high}`;
                        trainingZones.zone2_bpm = `${cc4Data.heartRateZones.hrZone1.low}-${cc4Data.heartRateZones.hrZone1.high}`;
                        trainingZones.zone3_bpm = `${cc4Data.heartRateZones.hrZone2.low}-${cc4Data.heartRateZones.hrZone2.high}`;
                        trainingZones.zone4_bpm = `${cc4Data.heartRateZones.hrZone3.low}-${cc4Data.heartRateZones.hrZone3.high}`;
                        trainingZones.zone5_bpm = `>${cc4Data.heartRateZones.hrZone4.low}`;
                      }
                      
                      // Extract recovery heart rates if available
                      if (cc4Data.recoveryHeartRates) {
                        trainingZones.recovery_1min = cc4Data.recoveryHeartRates.rec1 || '';
                        trainingZones.recovery_2min = cc4Data.recoveryHeartRates.rec2 || '';
                      }
                      
                      
                      // Update patient information if available
                      const patientInfo = {};
                      if (cc4Data.patientInfo) {
                        patientInfo.patient_name = `${cc4Data.patientInfo.firstName || ''} ${cc4Data.patientInfo.lastName || ''}`;
                        patientInfo.patient_gender = cc4Data.patientInfo.gender || '';
                        patientInfo.patient_age = cc4Data.patientInfo.age || '';
                        patientInfo.patient_height = cc4Data.patientInfo.height || '';
                        patientInfo.patient_weight = cc4Data.patientInfo.weight || '';
                      }
                      
                      // Update exam date if available
                      let examDate = prev.exam_date;
                      if (cc4Data.examDate) {
                        try {
                          examDate = format(new Date(cc4Data.examDate), 'yyyy-MM-dd');
                        } catch (e) {
                          console.error('Failed to parse exam date:', e);
                        }
                      }
                      
                      // Limit table data to 10 items for unique time values
                      const limitedTableData = [];
                      const uniqueTimes = new Set();
                      
                      for (const item of tableData) {
                        if (!uniqueTimes.has(item.time) && limitedTableData.length < 16) {
                          uniqueTimes.add(item.time);
                          limitedTableData.push(item);
                        }
                      }
                      
                      // Get graph data from VO2_TEST_RESULTS section
                      const graphData = {
                        vo2Max: cc4Data.vo2Max || '',
                        heartRateMax: cc4Data.heartRateMax || '',
                        anaerobicThreshold: cc4Data.anaerobicThreshold || '',
                        anaerobicThresholdHR: cc4Data.anaerobicThresholdHR || '',
                        aerobicThreshold: cc4Data.aerobicThreshold || '',
                        aerobicThresholdHR: cc4Data.aerobicThresholdHR || ''
                      };
                      
                      // Return the updated form data
                      return {
                        ...prev,
                        ...patientInfo,
                        exam_date: examDate,
                        vo2_data_table: limitedTableData,
                        stage_data: processedStageData,
                        training_zones: trainingZones,
                        vo2max_value: graphData.vo2Max,
                        heart_rate_max: graphData.heartRateMax,
                        anaerobic_threshold: graphData.anaerobicThreshold,
                        anaerobic_threshold_hr: graphData.anaerobicThresholdHR,
                        aerobic_threshold: graphData.aerobicThreshold,
                        aerobic_threshold_hr: graphData.aerobicThresholdHR
                      };
                    });
                  }}
                />
              )}
              
            <div className="mb-4 flex justify-between items-start">
              <div>
                <h2 className="text-xl font-semibold">
                  {sections[currentSection].label}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {isEditingAllowed
                    ? "Complete all fields in this section before proceeding"
                    : "View the information in this section"}
                </p>
              </div>
              
              {/* Show upload button only for VO2 Data Table section */}
              {sections[currentSection].component === 'vo2_data_table' && (
                <div className="ml-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={!isEditingAllowed || isProcessingImage}
                  />
                  <Button
                    variant="secondary"
                    onClick={handleUploadClick}
                    disabled={!isEditingAllowed || isProcessingImage}
                    className="flex items-center gap-2"
                  >
                    {isProcessingImage ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaUpload />
                        Upload Table Image
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            <FormSection>
              <div className="space-y-6">
                {sections[currentSection].component === 'vo2_data_table' ? (
                  <VO2DataTable
                    value={formData.vo2_data_table}
                    onChange={(value) => handleFormChange('vo2_data_table', value)}
                    disabled={!isEditingAllowed}
                  />
                ) : sections[currentSection].component === 'training_zones' ? (
                  <TrainingZonesSection
                    value={formData.training_zones}
                    onChange={(value) => handleFormChange('training_zones', value)}
                    disabled={!isEditingAllowed}
                  />
                ) : sections[currentSection].component === 'vo2_graph' ? (
                  <div>
                    <h3 className="text-lg font-medium mb-4">VO2 Max Graph</h3>
                    <VO2Graph 
                      stageData={stageData} 
                      thresholds={{
                        aerobic: formData.aerobic_threshold_hr ? parseInt(formData.aerobic_threshold_hr) : null,
                        anaerobic: formData.anaerobic_threshold_hr ? parseInt(formData.anaerobic_threshold_hr) : null
                      }}
                    />
                    <div className="mt-4 text-sm text-gray-500">
                      <p>Note: This graph shows the relationship between heart rate, VO2, and thresholds during the test.</p>
                    </div>
                  </div>
                ) : (
                  sections[currentSection].fields.map(field => renderField(field))
                )}
              </div>
            </FormSection>

            <div className="flex justify-between mt-6">
              <Button
                variant="secondary"
                onClick={currentSection > 0 ? goToPreviousSection : handleCancel}
              >
                {currentSection > 0 ? 'Previous' : 'Back to List'}
              </Button>

              {currentSection === sections.length - 1 ? (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handlePreview}
                    disabled={submitting}
                  >
                    Preview Report
                  </Button>
                  <Button
                    onClick={() => handleSubmit('submitted')}
                    disabled={submitting}
                    variant="secondary"
                  >
                    {submitting ? 'Saving...' : 'Submit Report'}
                  </Button>
                  <Button
                    onClick={() => handleSubmit('published')}
                    disabled={submitting}
                    className="bg-primary-500 hover:bg-primary-600"
                  >
                    {submitting ? 'Publishing...' : 'Submit & Publish'}
                  </Button>
                </div>
              ) : (
                <Button onClick={goToNextSection}>Next</Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default VO2MaxReportForm;
